const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const numberOrZero = (value) => toFiniteNumber(value) ?? 0;

const roundToTwo = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return 0;
  }
  return Math.round(numeric * 100) / 100;
};

const clampToZero = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return 0;
  }
  return numeric < 0 ? 0 : numeric;
};

const deriveDaysRemaining = (budgetRemaining, remainingPerDay) => {
  const remaining = toFiniteNumber(budgetRemaining);
  const perDay = toFiniteNumber(remainingPerDay);
  if (remaining === null || perDay === null || perDay <= 0) {
    return null;
  }
  const value = remaining / perDay;
  return Number.isFinite(value) ? value : null;
};

const extractPacingMeta = (metrics) => {
  if (!metrics || typeof metrics !== 'object') {
    return {};
  }

  const meta = metrics.__pacing_metadata || metrics.pacing_metadata;
  if (meta && typeof meta === 'object') {
    return meta;
  }

  return {};
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getDaysLeftInMonthIncludingToday = (inputDate) => {
  const reference =
    inputDate instanceof Date
      ? inputDate
      : inputDate
        ? new Date(inputDate)
        : new Date();
  if (!(reference instanceof Date) || Number.isNaN(reference.getTime())) {
    return null;
  }

  const year = reference.getFullYear();
  const month = reference.getMonth();
  const startOfTodayUTC = Date.UTC(year, month, reference.getDate());
  const startOfNextMonthUTC = Date.UTC(year, month + 1, 1);
  const diff = Math.floor((startOfNextMonthUTC - startOfTodayUTC) / MS_PER_DAY);

  if (!Number.isFinite(diff)) {
    return null;
  }

  return diff > 0 ? diff : 0;
};

const recalculateBudgetMetrics = (row, metrics, merged, options = {}) => {
  if (!row || !metrics) {
    return merged;
  }

  const { shouldFallbackToActuals = false, calendarToday } = options;

  const budgetAmount = toFiniteNumber(
    metrics.budget_amount ?? metrics.budget ?? merged.budget_amount ?? merged.budget
  );

  if (budgetAmount === null) {
    return merged;
  }

  const feeValue = toFiniteNumber(row.client_fee ?? merged.client_fee);
  const fee = feeValue ?? 0;
  const feeMultiplier = 1 + fee;

  const baseMeta = extractPacingMeta(metrics);
  const metaMonthThroughYesterdayWithFees = toFiniteNumber(
    metrics.month_through_yesterday_with_fees ??
      baseMeta.month_through_yesterday_with_fees ??
      baseMeta.actual_through_yesterday_with_fees
  );
  const metaActualMonthToDateWithFees = toFiniteNumber(
    metrics.actual_month_to_date_with_fees ??
      baseMeta.actual_month_to_date_with_fees ??
      baseMeta.spent_with_fees
  );
  const metaPacingMonthToDateWithFees = toFiniteNumber(
    metrics.pacing_month_to_date_with_fees ??
      metrics.month_to_date_with_fees ??
      baseMeta.pacing_month_to_date_with_fees ??
      baseMeta.month_to_date_with_fees ??
      baseMeta.month_through_yesterday_with_fees
  );
  const metaTodaySpendWithFees = toFiniteNumber(
    metrics.today_spend_with_fees ?? baseMeta.today_spend_with_fees
  );

  const parsedYesterdayWithFees = toFiniteNumber(
    baseMeta.yesterday_spend_with_fees ??
      metrics.yesterday_spend_with_fees ??
      merged.yesterday_spend_with_fees
  );
  const parsedYesterday = toFiniteNumber(
    baseMeta.yesterday_spend ?? metrics.yesterday_spend ?? merged.yesterday_spend
  );

  const hasWithFees = typeof parsedYesterdayWithFees === 'number';
  const hasRawYesterday = typeof parsedYesterday === 'number';

  const yesterdaySpendRaw = hasRawYesterday ? clampToZero(parsedYesterday) : 0;
  const yesterdaySpendWithFees = hasWithFees
    ? clampToZero(parsedYesterdayWithFees)
    : yesterdaySpendRaw * feeMultiplier;
  const hasValidYesterday = hasWithFees || hasRawYesterday;

  let daysRemaining = baseMeta.days_remaining;
  if (!(typeof daysRemaining === 'number' && Number.isFinite(daysRemaining))) {
    daysRemaining = deriveDaysRemaining(
      metrics.budget_remaining ?? merged.budget_remaining,
      metrics.remaining_per_day ?? merged.remaining_per_day
    );
  }

  const hasValidDaysRemaining =
    typeof daysRemaining === 'number' && Number.isFinite(daysRemaining);
  const safeDaysRemaining = hasValidDaysRemaining ? Math.max(daysRemaining, 0) : 0;
  const forecastDays = hasValidDaysRemaining ? safeDaysRemaining : 0;

  const calendarDaysRemainingRaw = getDaysLeftInMonthIncludingToday(calendarToday);
  const hasCalendarDaysRemaining =
    typeof calendarDaysRemainingRaw === 'number' && Number.isFinite(calendarDaysRemainingRaw);
  const calendarDaysRemaining = hasCalendarDaysRemaining
    ? Math.max(calendarDaysRemainingRaw, 0)
    : null;

  const fallbackActualRaw = toFiniteNumber(
    row.total_spend_sum ?? merged.total_spend_sum
  );
  const fallbackActual =
    typeof fallbackActualRaw === 'number' && Number.isFinite(fallbackActualRaw)
      ? fallbackActualRaw
      : null;

  const pacingMonthToDateCandidate = toFiniteNumber(
    metrics.pacing_month_to_date_spend ??
      metrics.month_to_date_spend ??
      baseMeta.pacing_month_to_date_spend ??
      baseMeta.month_to_date_spend
  );
  let resolvedPacing = pacingMonthToDateCandidate;
  if (
    resolvedPacing === null &&
    typeof metaPacingMonthToDateWithFees === 'number' &&
    Number.isFinite(metaPacingMonthToDateWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derived = metaPacingMonthToDateWithFees / divisor;
      if (Number.isFinite(derived)) {
        resolvedPacing = derived;
      }
    }
  }

  let todaySpendCandidate = toFiniteNumber(
    metrics.today_spend ?? baseMeta.today_spend
  );

  if (
    todaySpendCandidate === null &&
    typeof metaTodaySpendWithFees === 'number' &&
    Number.isFinite(metaTodaySpendWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedToday = metaTodaySpendWithFees / divisor;
      if (Number.isFinite(derivedToday)) {
        todaySpendCandidate = derivedToday;
      }
    }
  }

  if (shouldFallbackToActuals && fallbackActual !== null) {
    if (todaySpendCandidate === null && typeof resolvedPacing === 'number') {
      const derivedToday = fallbackActual - resolvedPacing;
      if (Number.isFinite(derivedToday)) {
        todaySpendCandidate = derivedToday;
      }
    }

    if (todaySpendCandidate === null && resolvedPacing === null) {
      todaySpendCandidate = fallbackActual;
    }

    if (resolvedPacing === null && typeof todaySpendCandidate === 'number') {
      const derivedPacing = fallbackActual - todaySpendCandidate;
      if (Number.isFinite(derivedPacing)) {
        resolvedPacing = derivedPacing;
      }
    }
  }

  const hasResolvedPacing =
    typeof resolvedPacing === 'number' && Number.isFinite(resolvedPacing);
  const hasTodaySpend =
    typeof todaySpendCandidate === 'number' && Number.isFinite(todaySpendCandidate);

  const pacingMonthToDate = clampToZero(hasResolvedPacing ? resolvedPacing : 0);
  const todaySpend = clampToZero(hasTodaySpend ? todaySpendCandidate : 0);

  const hasTodaySpendWithFeesMeta =
    typeof metaTodaySpendWithFees === 'number' && Number.isFinite(metaTodaySpendWithFees);
  const hasPacingWithFeesMeta =
    typeof metaPacingMonthToDateWithFees === 'number' &&
    Number.isFinite(metaPacingMonthToDateWithFees);
  const hasMonthThroughYesterdayWithFeesMeta =
    typeof metaMonthThroughYesterdayWithFees === 'number' &&
    Number.isFinite(metaMonthThroughYesterdayWithFees);
  const todaySpendWithFees = hasTodaySpendWithFeesMeta
    ? clampToZero(metaTodaySpendWithFees)
    : todaySpend * feeMultiplier;

  let actualMonthToDate = pacingMonthToDate + todaySpend;
  if (shouldFallbackToActuals && fallbackActual !== null) {
    actualMonthToDate = Math.max(actualMonthToDate, fallbackActual, 0);
  }

  if (
    typeof metaActualMonthToDateWithFees === 'number' &&
    Number.isFinite(metaActualMonthToDateWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedActual = metaActualMonthToDateWithFees / divisor;
      if (Number.isFinite(derivedActual)) {
        actualMonthToDate = Math.max(actualMonthToDate, derivedActual, 0);
      }
    }
  }

  const spentWithFees =
    typeof metaActualMonthToDateWithFees === 'number' &&
    Number.isFinite(metaActualMonthToDateWithFees)
      ? clampToZero(metaActualMonthToDateWithFees)
      : actualMonthToDate * feeMultiplier;
  const pacingMonthToDateWithFees = hasPacingWithFeesMeta
    ? clampToZero(metaPacingMonthToDateWithFees)
    : pacingMonthToDate * feeMultiplier;

  let actualThroughYesterday = Math.max(
    actualMonthToDate - todaySpend,
    pacingMonthToDate,
    0
  );
  if (hasMonthThroughYesterdayWithFeesMeta) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedThrough = metaMonthThroughYesterdayWithFees / divisor;
      if (Number.isFinite(derivedThrough)) {
        actualThroughYesterday = Math.max(actualThroughYesterday, derivedThrough, 0);
      }
    }
  }
  const computedMonthThroughYesterdayWithFees = Math.max(
    spentWithFees - todaySpendWithFees,
    pacingMonthToDateWithFees,
    actualThroughYesterday * feeMultiplier,
    0
  );
  const monthThroughYesterdayWithFees = hasMonthThroughYesterdayWithFeesMeta
    ? clampToZero(metaMonthThroughYesterdayWithFees)
    : clampToZero(computedMonthThroughYesterdayWithFees);
  const actualThroughYesterdayWithFees = monthThroughYesterdayWithFees;

  merged.month_to_date_spend = roundToTwo(spentWithFees);
  merged.budget_amount = roundToTwo(budgetAmount);

  const budgetRemaining = clampToZero(budgetAmount - spentWithFees);

  const projectionDaysRaw =
    calendarDaysRemaining !== null ? calendarDaysRemaining : forecastDays;
  const hasProjectionDays =
    typeof projectionDaysRaw === 'number' && Number.isFinite(projectionDaysRaw);
  const projectionDays = hasProjectionDays ? Math.max(projectionDaysRaw, 0) : null;

  const projectedCandidate =
    projectionDays !== null
      ? monthThroughYesterdayWithFees + yesterdaySpendWithFees * projectionDays
      : monthThroughYesterdayWithFees;

  let projected = Number.isFinite(projectedCandidate)
    ? projectedCandidate
    : monthThroughYesterdayWithFees;
  projected = Math.max(projected, spentWithFees, monthThroughYesterdayWithFees);
  projected = clampToZero(projected);

  const remainingPerDay =
    forecastDays > 0 ? clampToZero(budgetRemaining / forecastDays) : 0;
  const pacingPercentage =
    budgetAmount > 0 ? clampToZero((projected / budgetAmount) * 100) : 0;

  const calendarBudgetRemainingPerDay =
    typeof calendarDaysRemaining === 'number' &&
    Number.isFinite(calendarDaysRemaining) &&
    calendarDaysRemaining > 0
      ? budgetRemaining / (feeMultiplier || 1) / calendarDaysRemaining
      : null;

  merged.budget_remaining = roundToTwo(budgetRemaining);
  merged.projected_spend = roundToTwo(projected);
  merged.remaining_per_day = roundToTwo(remainingPerDay);
  merged.pacing_percentage = roundToTwo(pacingPercentage);
  if (
    typeof calendarBudgetRemainingPerDay === 'number' &&
    Number.isFinite(calendarBudgetRemainingPerDay)
  ) {
    merged.calendar_budget_remaining_per_day = roundToTwo(
      calendarBudgetRemainingPerDay
    );
  } else {
    delete merged.calendar_budget_remaining_per_day;
  }
  if (hasValidYesterday) {
    merged.yesterday_spend = roundToTwo(yesterdaySpendWithFees);
    merged.yesterday_spend_with_fees = roundToTwo(yesterdaySpendWithFees);
    if (hasRawYesterday) {
      merged.yesterday_spend_without_fees = roundToTwo(yesterdaySpendRaw);
    } else {
      delete merged.yesterday_spend_without_fees;
    }
  } else {
    delete merged.yesterday_spend_with_fees;
    delete merged.yesterday_spend_without_fees;
  }

  const nextMeta = {
    ...baseMeta,
    spent_with_fees: spentWithFees,
    actual_month_to_date_with_fees: spentWithFees,
    actual_month_to_date_spend: actualMonthToDate,
    actual_through_yesterday_spend: actualThroughYesterday,
    actual_through_yesterday_with_fees: actualThroughYesterdayWithFees,
    month_through_yesterday_with_fees: monthThroughYesterdayWithFees,
  };

  if (hasValidDaysRemaining) {
    nextMeta.days_remaining = safeDaysRemaining;
    nextMeta.safe_days_remaining = safeDaysRemaining;
    nextMeta.forecast_days = forecastDays;
  } else {
    delete nextMeta.days_remaining;
    delete nextMeta.safe_days_remaining;
    delete nextMeta.forecast_days;
  }

  if (hasValidYesterday) {
    nextMeta.yesterday_spend = yesterdaySpendWithFees;
    nextMeta.yesterday_spend_with_fees = yesterdaySpendWithFees;
    if (hasRawYesterday) {
      nextMeta.yesterday_spend_without_fees = yesterdaySpendRaw;
    } else {
      delete nextMeta.yesterday_spend_without_fees;
    }
  } else {
    delete nextMeta.yesterday_spend;
    delete nextMeta.yesterday_spend_with_fees;
    delete nextMeta.yesterday_spend_without_fees;
  }

  if (
    hasResolvedPacing ||
    hasPacingWithFeesMeta ||
    (shouldFallbackToActuals && fallbackActual !== null)
  ) {
    nextMeta.pacing_month_to_date_spend = pacingMonthToDate;
    nextMeta.month_to_date_spend = pacingMonthToDate;
    nextMeta.pacing_month_to_date_with_fees = pacingMonthToDateWithFees;
  } else {
    delete nextMeta.pacing_month_to_date_spend;
    delete nextMeta.month_to_date_spend;
    delete nextMeta.pacing_month_to_date_with_fees;
  }

  if (
    hasTodaySpend ||
    hasTodaySpendWithFeesMeta ||
    (shouldFallbackToActuals && fallbackActual !== null)
  ) {
    nextMeta.today_spend = todaySpend;
    nextMeta.today_spend_with_fees = todaySpendWithFees;
  } else {
    delete nextMeta.today_spend;
    delete nextMeta.today_spend_with_fees;
  }

  merged.__pacing_metadata = nextMeta;
  return merged;
};

const buildPacingLookup = (budgets) => {
  const clientMap = new Map();
  const channelMap = new Map();
  const territoryMap = new Map();
  const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();

  const ensureBucket = (map, key) => {
    if (!map.has(key)) {
      map.set(key, {
        budget_amount: 0,
        projected_spend: 0,
        month_to_date_spend: 0,
        today_spend: 0,
        has_today_spend: false,
        today_spend_with_fees: 0,
        has_today_spend_with_fees: false,
        budget_remaining: 0,
        remaining_per_day: 0,
        yesterday_spend: 0,
        yesterday_spend_with_fees: 0,
        has_yesterday_with_fees: false,
        pacing_month_to_date_with_fees: 0,
        has_pacing_month_to_date_with_fees: false,
        month_through_yesterday_with_fees: 0,
        has_month_through_yesterday_with_fees: false,
        actual_month_to_date_with_fees: 0,
        has_actual_month_to_date_with_fees: false,
        verticals: new Map(),
      });
    }
    return map.get(key);
  };

  const addMetrics = (bucket, metrics) => {
    bucket.budget_amount += metrics.amount;
    bucket.projected_spend += metrics.projected_spend;
    bucket.month_to_date_spend += metrics.month_to_date_spend;
    bucket.budget_remaining += metrics.budget_remaining;
    bucket.remaining_per_day += metrics.remaining_per_day;
    bucket.yesterday_spend += metrics.yesterday_spend;
    if (
      typeof metrics.yesterday_spend_with_fees === 'number' &&
      Number.isFinite(metrics.yesterday_spend_with_fees)
    ) {
      bucket.yesterday_spend_with_fees += metrics.yesterday_spend_with_fees;
      bucket.has_yesterday_with_fees = true;
    }
    if (typeof metrics.today_spend === 'number' && Number.isFinite(metrics.today_spend)) {
      bucket.today_spend += metrics.today_spend;
      bucket.has_today_spend = true;
    }
    if (
      typeof metrics.today_spend_with_fees === 'number' &&
      Number.isFinite(metrics.today_spend_with_fees)
    ) {
      bucket.today_spend_with_fees += metrics.today_spend_with_fees;
      bucket.has_today_spend_with_fees = true;
    }
    if (
      typeof metrics.pacing_month_to_date_with_fees === 'number' &&
      Number.isFinite(metrics.pacing_month_to_date_with_fees)
    ) {
      bucket.pacing_month_to_date_with_fees += metrics.pacing_month_to_date_with_fees;
      bucket.has_pacing_month_to_date_with_fees = true;
    }
    if (
      typeof metrics.month_through_yesterday_with_fees === 'number' &&
      Number.isFinite(metrics.month_through_yesterday_with_fees)
    ) {
      bucket.month_through_yesterday_with_fees +=
        metrics.month_through_yesterday_with_fees;
      bucket.has_month_through_yesterday_with_fees = true;
    }
    if (
      typeof metrics.actual_month_to_date_with_fees === 'number' &&
      Number.isFinite(metrics.actual_month_to_date_with_fees)
    ) {
      bucket.actual_month_to_date_with_fees += metrics.actual_month_to_date_with_fees;
      bucket.has_actual_month_to_date_with_fees = true;
    }
  };

  budgets.forEach((budget) => {
    const metrics = {
      amount: numberOrZero(budget.amount),
      projected_spend: numberOrZero(budget.projected_spend),
      month_to_date_spend: numberOrZero(budget.month_to_date_spend),
      budget_remaining: numberOrZero(budget.budget_remaining),
      remaining_per_day: numberOrZero(budget.remaining_per_day),
      yesterday_spend: numberOrZero(
        budget.yesterday_spend ??
          budget?.__pacing_metadata?.yesterday_spend_without_fees ??
          budget?.pacing_metadata?.yesterday_spend_without_fees
      ),
      yesterday_spend_with_fees: toFiniteNumber(
        budget.yesterday_spend_with_fees ??
          budget?.__pacing_metadata?.yesterday_spend_with_fees ??
          budget?.pacing_metadata?.yesterday_spend_with_fees ??
          budget?.__pacing_metadata?.yesterday_spend ??
          budget?.pacing_metadata?.yesterday_spend
      ),
      today_spend: toFiniteNumber(
        budget.today_spend ??
          budget?.__pacing_metadata?.today_spend ??
          budget?.pacing_metadata?.today_spend
      ),
      today_spend_with_fees: toFiniteNumber(
        budget.today_spend_with_fees ??
          budget?.__pacing_metadata?.today_spend_with_fees ??
          budget?.pacing_metadata?.today_spend_with_fees
      ),
      pacing_month_to_date_with_fees: toFiniteNumber(
        budget.pacing_month_to_date_with_fees ??
          budget?.__pacing_metadata?.pacing_month_to_date_with_fees ??
          budget?.pacing_metadata?.pacing_month_to_date_with_fees ??
          budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
          budget?.pacing_metadata?.month_through_yesterday_with_fees
      ),
      month_through_yesterday_with_fees: toFiniteNumber(
        budget.month_through_yesterday_with_fees ??
          budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
          budget?.__pacing_metadata?.actual_through_yesterday_with_fees ??
          budget?.pacing_metadata?.month_through_yesterday_with_fees ??
          budget?.pacing_metadata?.actual_through_yesterday_with_fees
      ),
      actual_month_to_date_with_fees: toFiniteNumber(
        budget.actual_month_to_date_with_fees ??
          budget?.__pacing_metadata?.actual_month_to_date_with_fees ??
          budget?.__pacing_metadata?.spent_with_fees ??
          budget?.pacing_metadata?.actual_month_to_date_with_fees ??
          budget?.pacing_metadata?.spent_with_fees
      ),
    };

    const clientKey = `client-${budget.client}`;
    const clientBucket = ensureBucket(clientMap, clientKey);
    addMetrics(clientBucket, metrics);

    const verticalName = (budget.vertical_data?.vertical || '').trim();
    if (verticalName) {
      const verticalBucket = ensureBucket(clientBucket.verticals, verticalName);
      addMetrics(verticalBucket, metrics);
    }

    if (budget.channel) {
      const channelNameKey = normalizeKey(
        budget?.channel_data?.name || budget?.channel_name || budget?.channel
      );
      const channelKey = `client-${budget.client}-channel-${channelNameKey || budget.channel}`;
      const channelBucket = ensureBucket(channelMap, channelKey);
      addMetrics(channelBucket, metrics);

      if (verticalName) {
        const channelVerticalBucket = ensureBucket(channelBucket.verticals, verticalName);
        addMetrics(channelVerticalBucket, metrics);
      }
    }

    const territoryNameKey = normalizeKey(
      budget?.location_data?.name || budget?.territory_name || budget?.location_name
    );
    if (territoryNameKey) {
      const territoryKey = `client-${budget.client}-territory-${territoryNameKey}`;
      const territoryBucket = ensureBucket(territoryMap, territoryKey);
      addMetrics(territoryBucket, metrics);

      if (verticalName) {
        const territoryVerticalBucket = ensureBucket(
          territoryBucket.verticals,
          verticalName
        );
        addMetrics(territoryVerticalBucket, metrics);
      }
    }
  });

  const finalizeBucket = (bucket) => {
    const amount = bucket.budget_amount;
    const projected = bucket.projected_spend;
    const budgetRemaining = bucket.budget_remaining;
    const remainingPerDay = bucket.remaining_per_day;
    const yesterdaySpend = bucket.has_yesterday_with_fees
      ? bucket.yesterday_spend_with_fees
      : bucket.yesterday_spend;
    const todaySpend = bucket.has_today_spend ? bucket.today_spend : null;
    const todaySpendWithFees = bucket.has_today_spend_with_fees
      ? bucket.today_spend_with_fees
      : null;
    const pacingMonthToDateWithFees = bucket.has_pacing_month_to_date_with_fees
      ? bucket.pacing_month_to_date_with_fees
      : null;
    const monthThroughYesterdayWithFees =
      bucket.has_month_through_yesterday_with_fees
        ? bucket.month_through_yesterday_with_fees
        : null;
    const actualMonthToDateWithFees = bucket.has_actual_month_to_date_with_fees
      ? bucket.actual_month_to_date_with_fees
      : null;
    const daysRemaining = deriveDaysRemaining(budgetRemaining, remainingPerDay);
    const pacingMeta = {};
    if (typeof daysRemaining === 'number' && Number.isFinite(daysRemaining)) {
      pacingMeta.days_remaining = daysRemaining;
    }
    if (typeof yesterdaySpend === 'number' && Number.isFinite(yesterdaySpend)) {
      pacingMeta.yesterday_spend = yesterdaySpend;
      if (bucket.has_yesterday_with_fees) {
        pacingMeta.yesterday_spend_with_fees = yesterdaySpend;
      }
    }
    if (typeof todaySpend === 'number' && Number.isFinite(todaySpend)) {
      pacingMeta.today_spend = todaySpend;
    }
    if (
      typeof todaySpendWithFees === 'number' &&
      Number.isFinite(todaySpendWithFees)
    ) {
      pacingMeta.today_spend_with_fees = todaySpendWithFees;
    }
    if (typeof bucket.month_to_date_spend === 'number' && Number.isFinite(bucket.month_to_date_spend)) {
      pacingMeta.pacing_month_to_date_spend = bucket.month_to_date_spend;
      pacingMeta.month_to_date_spend = bucket.month_to_date_spend;
      if (
        typeof pacingMonthToDateWithFees === 'number' &&
        Number.isFinite(pacingMonthToDateWithFees)
      ) {
        pacingMeta.pacing_month_to_date_with_fees = pacingMonthToDateWithFees;
      }
      const actualThroughYesterdaySpend = Math.max(
        typeof todaySpend === 'number' && Number.isFinite(todaySpend)
          ? bucket.month_to_date_spend - todaySpend
          : bucket.month_to_date_spend,
        bucket.month_to_date_spend,
        0
      );
      if (Number.isFinite(actualThroughYesterdaySpend)) {
        pacingMeta.actual_through_yesterday_spend = actualThroughYesterdaySpend;
      }
    }
    if (
      typeof monthThroughYesterdayWithFees === 'number' &&
      Number.isFinite(monthThroughYesterdayWithFees)
    ) {
      pacingMeta.month_through_yesterday_with_fees = monthThroughYesterdayWithFees;
      pacingMeta.actual_through_yesterday_with_fees = monthThroughYesterdayWithFees;
    } else if (
      typeof pacingMonthToDateWithFees === 'number' &&
      Number.isFinite(pacingMonthToDateWithFees)
    ) {
      pacingMeta.month_through_yesterday_with_fees = pacingMonthToDateWithFees;
    }
    if (
      typeof actualMonthToDateWithFees === 'number' &&
      Number.isFinite(actualMonthToDateWithFees)
    ) {
      pacingMeta.actual_month_to_date_with_fees = actualMonthToDateWithFees;
      pacingMeta.spent_with_fees = actualMonthToDateWithFees;
    }
    const projectedRemainingBudget =
      typeof amount === 'number' && Number.isFinite(amount) &&
      typeof projected === 'number' && Number.isFinite(projected)
        ? amount - projected
        : null;

    const result = {
      budget_amount: roundToTwo(amount),
      projected_spend: roundToTwo(projected),
      month_to_date_spend: roundToTwo(bucket.month_to_date_spend),
      budget_remaining: roundToTwo(budgetRemaining),
      remaining_per_day: roundToTwo(remainingPerDay),
      pacing_percentage: amount > 0 ? roundToTwo((projected / amount) * 100) : 0,
      yesterday_spend: roundToTwo(yesterdaySpend),
    };
    if (
      typeof projectedRemainingBudget === 'number' &&
      Number.isFinite(projectedRemainingBudget)
    ) {
      result.calendar_budget_remaining = roundToTwo(projectedRemainingBudget);
    }
    if (todaySpend !== null) {
      result.today_spend = roundToTwo(todaySpend);
    }
    if (
      typeof todaySpendWithFees === 'number' &&
      Number.isFinite(todaySpendWithFees)
    ) {
      result.today_spend_with_fees = roundToTwo(todaySpendWithFees);
    }
    if (bucket.has_yesterday_with_fees) {
      result.yesterday_spend_with_fees = roundToTwo(
        bucket.yesterday_spend_with_fees
      );
    }
    if (Object.keys(pacingMeta).length > 0) {
      result.__pacing_metadata = pacingMeta;
    }

    if (bucket.verticals.size) {
      result.vertical_budget_breakdown = Array.from(bucket.verticals.entries()).map(
        ([name, vertBucket]) => ({
          vertical: name,
          ...finalizeBucket(vertBucket),
        })
      );
    }

    return result;
  };

  const finalizeMap = (map) => {
    const output = {};
    map.forEach((bucket, key) => {
      output[key] = finalizeBucket(bucket);
    });
    return output;
  };

  return {
    clients: finalizeMap(clientMap),
    channels: finalizeMap(channelMap),
    territories: finalizeMap(territoryMap),
  };
};

export {
  buildPacingLookup,
  clampToZero,
  deriveDaysRemaining,
  extractPacingMeta,
  getDaysLeftInMonthIncludingToday,
  numberOrZero,
  recalculateBudgetMetrics,
  roundToTwo,
  toFiniteNumber,
};
